import { isImageMimeType } from '@gitbook/app-database';
import {
    type Edits,
    type FilesDiffResult,
    getLastEdit,
    type ReusableContentsDiffResult,
    type TagsDiffResult,
    type VariablesDiffResult,
} from '@gitbook/hive-core';

import type { DiffChangeType } from '../Diff';
import type { LibraryItem } from './LibraryItems';
import { type SortBy, type SortDirection, sortLibraryItems } from './Sorting';

/**
 * Returns the last updated at from edit or 0.
 */
export function getLastUpdatedAtFromEdits(edits: Edits): number {
    const lastEdit = getLastEdit(edits);
    return lastEdit ? lastEdit.updatedAt : 0;
}

/**
 * Stable sort placing items with a diff before items without, preserving the
 * input order within each group.
 */
export function sortByDiffFirst<T>(
    items: T[],
    getDiffType: (item: T) => DiffChangeType | null
): T[] {
    return [...items].sort((a, b) => {
        const aWeight = getDiffType(a) !== null ? 0 : 1;
        const bWeight = getDiffType(b) !== null ? 0 : 1;
        return aWeight - bWeight;
    });
}

/**
 * `filesDiff` covers both files and images. Walk the diff map once and report
 * which kinds actually changed.
 */
export function splitFilesDiffByKind(filesDiff: FilesDiffResult | null | undefined): {
    hasFileChange: boolean;
    hasImageChange: boolean;
} {
    let hasFileChange = false;
    let hasImageChange = false;
    if (!filesDiff) {
        return { hasFileChange, hasImageChange };
    }
    for (const [, change] of filesDiff.diffs) {
        if (isImageMimeType(change.item.file.contentType)) {
            hasImageChange = true;
        } else {
            hasFileChange = true;
        }
        if (hasFileChange && hasImageChange) break;
    }
    return { hasFileChange, hasImageChange };
}

/**
 * Whether any library item (variable, tag, reusable content, file or image) has changed.
 */
export function hasAnyLibraryChange(diffs: {
    variablesDiff: VariablesDiffResult | null | undefined;
    tagsDiff: TagsDiffResult | null | undefined;
    reusableContentsDiff: ReusableContentsDiffResult | null | undefined;
    filesDiff: FilesDiffResult | null | undefined;
}): boolean {
    return (
        (diffs.variablesDiff?.diffs.size ?? 0) > 0 ||
        (diffs.tagsDiff?.diffs.size ?? 0) > 0 ||
        (diffs.reusableContentsDiff?.diffs.size ?? 0) > 0 ||
        (diffs.filesDiff?.diffs.size ?? 0) > 0
    );
}

interface LibraryDiffStrategy<TItem extends LibraryItem, TChange extends { type: DiffChangeType }> {
    getId: (item: TItem) => string;
    createDeletedItem: (
        id: string,
        change: Extract<TChange, { type: 'deleted' }>
    ) => Omit<TItem, 'diffType'> | null;
    /** Extra per-type fields to merge onto a changed item */
    getExtraFields?: (item: TItem, change: TChange) => Partial<TItem>;
}

/**
 * Build the items list for a library view in diff mode.
 */
export function buildLibraryDiffItems<
    TItem extends LibraryItem,
    TChange extends { type: DiffChangeType },
>(
    items: { base: TItem[]; diff: { diffs: Map<string, TChange> } | null },
    strategy: LibraryDiffStrategy<TItem, TChange>,
    options: {
        sortBy: SortBy;
        sortDirection: SortDirection;
        onlyShowChanges: boolean;
    }
): TItem[] {
    const { base, diff } = items;
    const { getId, createDeletedItem, getExtraFields } = strategy;
    const { sortBy, sortDirection, onlyShowChanges } = options;

    const out: TItem[] = [];

    for (const item of base) {
        const change = diff?.diffs.get(getId(item));
        if (!change) {
            out.push(item);
            continue;
        }
        const next = { ...item, diffType: change.type } as TItem;
        out.push(getExtraFields ? { ...next, ...getExtraFields(item, change) } : next);
    }

    diff?.diffs.forEach((change, id) => {
        if (change.type !== 'deleted') return;
        const item = createDeletedItem(id, change as Extract<TChange, { type: 'deleted' }>);
        if (item) out.push({ ...item, diffType: 'deleted' } as TItem);
    });

    const sortedItems = sortLibraryItems(out, { sortBy, sortDirection });
    const results = sortByDiffFirst(sortedItems, (i) => i.diffType ?? null);

    return onlyShowChanges ? results.filter((i) => i.diffType) : results;
}
